import React, { useState, useRef, useEffect } from 'react';
import { Card, Select, Button, Space, message } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, FullscreenOutlined } from '@ant-design/icons';
import type { VideoInfo } from '../../services/knowledgeBase/knowledgeBaseService';

interface VideoPlayerProps {
  video: VideoInfo;
  width?: string | number;
  height?: string | number;
  controls?: boolean;
  autoplay?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  video,
  width = '100%',
  height = 'auto',
  controls = true,
  autoplay = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentQuality, setCurrentQuality] = useState<string>('original');
  const [videoUrl, setVideoUrl] = useState(video.url);

  // 如果有多个清晰度版本，使用第一个作为默认
  useEffect(() => {
    if (video.variants && video.variants.length > 0) {
      // 默认选择最高清晰度
      const highestQuality = video.variants[0];
      setCurrentQuality(highestQuality.quality);
      setVideoUrl(highestQuality.url);
    } else {
      setVideoUrl(video.url);
    }
  }, [video]);

  // 切换清晰度
  const handleQualityChange = (quality: string) => {
    if (quality === 'original') {
      setVideoUrl(video.url);
    } else {
      const variant = video.variants?.find(v => v.quality === quality);
      if (variant) {
        setVideoUrl(variant.url);
      }
    }
    setCurrentQuality(quality);
  };

  // 播放/暂停
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  // 全屏
  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if ((videoRef.current as any).webkitRequestFullscreen) {
        (videoRef.current as any).webkitRequestFullscreen();
      } else if ((videoRef.current as any).mozRequestFullScreen) {
        (videoRef.current as any).mozRequestFullScreen();
      }
    }
  };

  // 视频事件处理
  const handlePlay = () => setPlaying(true);
  const handlePause = () => setPlaying(false);
  const handleEnded = () => setPlaying(false);
  const handleError = () => {
    message.error('视频加载失败，请检查网络连接或视频文件');
  };

  return (
    <Card>
      <div style={{ position: 'relative' }}>
        <video
          ref={videoRef}
          src={videoUrl}
          width={width}
          height={height}
          controls={controls}
          autoPlay={autoplay}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={handleError}
          style={{
            width: '100%',
            maxWidth: '100%',
            borderRadius: '4px',
          }}
        >
          您的浏览器不支持视频播放
        </video>

        {!controls && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
            }}
          >
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={handlePlayPause}
            />
          </div>
        )}
      </div>

      {/* 视频信息 */}
      <div style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <strong>文件名：</strong>
            {video.fileName}
          </div>
          {video.fileSize > 0 && (
            <div>
              <strong>文件大小：</strong>
              {(video.fileSize / (1024 * 1024)).toFixed(2)} MB
            </div>
          )}
          {video.duration > 0 && (
            <div>
              <strong>时长：</strong>
              {Math.floor(video.duration / 60)}:{(video.duration % 60).toFixed(0).padStart(2, '0')}
            </div>
          )}
          {video.resolution.width > 0 && video.resolution.height > 0 && (
            <div>
              <strong>分辨率：</strong>
              {video.resolution.width} × {video.resolution.height}
            </div>
          )}

          {/* 清晰度选择 */}
          {video.variants && video.variants.length > 0 && (
            <div>
              <strong>清晰度：</strong>
              <Select
                value={currentQuality}
                onChange={handleQualityChange}
                style={{ width: 120, marginLeft: 8 }}
              >
                <Select.Option value="original">原始</Select.Option>
                {video.variants.map((variant) => (
                  <Select.Option key={variant.quality} value={variant.quality}>
                    {variant.quality}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}

          {/* 操作按钮 */}
          <Space>
            {!controls && (
              <Button
                icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={handlePlayPause}
              >
                {playing ? '暂停' : '播放'}
              </Button>
            )}
            <Button icon={<FullscreenOutlined />} onClick={handleFullscreen}>
              全屏
            </Button>
          </Space>
        </Space>
      </div>
    </Card>
  );
};

export default VideoPlayer;

